import React from 'react';
import { FlowFilters } from '../../../../../types/flow';
import { FiX } from 'react-icons/fi';
import { cn } from '../lib/utils';

interface AdSlotFiltersProps {
  filters?: FlowFilters;
  onFiltersChange: (filters: FlowFilters) => void;
}

export const filterOptions = {
  adSlots: [
    'Portal/HomeSeamlessCarousel@Main',
    'Portal/HomeBigCarousel@Main',
    'Portal/HomeRecommend@Main',
    'Portal/DiscoveryCarousel@Main',
    'Portal/Discovery@Main',
    'Portal/ScreenSaver@Main',
    'Portal/HomeSecondLine@Main',
    'Portal/Wedge@Main',
  ],
  osVersions: ['4.0', '4.5', '5.0', '6.0', '22', '23', '24', '25', '26'],
  makes: ['LG'],
  adTypes: [
    'clickableVideoTo',
    'companionTo',
    'bannerTo',
    'expandableBannerTo',
  ],
  destTypes: ['Web', 'WebFS', 'Image', 'Video', 'App', 'Deeplink'],
  countries: [
    'USA',
    'IND',
    'GBR',
    'MEX',
    'AUS',
    'ESP',
    'DEU',
    'CAN',
    'BRA',
    'TUR',
    'ITA',
    'POL',
    'FRA',
    'KOR',
    'SWE',
    'NLD',
  ],
};

interface MultiSelectProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  mode?: 'single' | 'multi';
}

export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  mode = 'multi',
}: MultiSelectProps) {
  const toggleOption = (option: string) => {
    if (mode === 'single') {
      // clicking the already-selected option clears it
      if (selected[0] === option) {
        onChange([]);
      } else {
        onChange([option]);
      }
      return;
    }

    if (selected.includes(option)) {
      onChange(selected.filter(s => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  const clearAll = () => onChange([]);

  return (
    <div className="adslot-multiselect">
      <div className="adslot-multiselect-header">
        <label className="adslot-multiselect-label">{label}</label>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="adslot-multiselect-clear"
          >
            Clear
          </button>
        )}
      </div>
      <div className="adslot-multiselect-selected">
        {selected.length === 0 ? (
          <span className="adslot-multiselect-placeholder">
            Select {label.toLowerCase()}...
          </span>
        ) : (
          selected.map(item => (
            <span
              key={item}
              className="chip chip-primary chip-sm adslot-multiselect-pill"
            >
              {item}
              <button
                type="button"
                onClick={() => toggleOption(item)}
                className="adslot-multiselect-pill-remove"
              >
                <FiX className="adslot-icon-xs" />
              </button>
            </span>
          ))
        )}
      </div>
      <div className="adslot-multiselect-options">
        {options
          .filter(opt => !selected.includes(opt))
          .map(option => (
            <button
              key={option}
              type="button"
              onClick={() => toggleOption(option)}
              className={cn('adslot-multiselect-option')}
            >
              {option}
            </button>
          ))}
      </div>
    </div>
  );
}

export const AdSlotFilters: React.FC<AdSlotFiltersProps> = ({
  filters,
  onFiltersChange,
}) => {
  const currentFilters: FlowFilters = filters || {
    adSlots: [],
    osVersions: [],
    makes: [],
    adTypes: [],
    destTypes: [],
    countries: [],
  };

  const updateFilter = (key: keyof FlowFilters, value: string[]) => {
    onFiltersChange({ ...currentFilters, [key]: value });
  };

  return (
    <div className="adslot-filters">
      <h3 className="adslot-filters-title">Flow Filters</h3>
      <div className="adslot-filters-grid">
        <MultiSelect
          label="AdSlot"
          options={filterOptions.adSlots}
          selected={currentFilters.adSlots}
          onChange={v => updateFilter('adSlots', v)}
          mode="single"
        />
        <MultiSelect
          label="OS Version(s)"
          options={filterOptions.osVersions}
          selected={currentFilters.osVersions}
          onChange={v => updateFilter('osVersions', v)}
        />
        <MultiSelect
          label="Make"
          options={filterOptions.makes}
          selected={currentFilters.makes}
          onChange={v => updateFilter('makes', v)}
          mode="single"
        />
        <MultiSelect
          label="Ad Type(s)"
          options={filterOptions.adTypes}
          selected={currentFilters.adTypes}
          onChange={v => updateFilter('adTypes', v)}
        />
        <MultiSelect
          label="Destination Type(s)"
          options={filterOptions.destTypes}
          selected={currentFilters.destTypes}
          onChange={v => updateFilter('destTypes', v)}
        />
        <MultiSelect
          label="Country(s)"
          options={filterOptions.countries}
          selected={currentFilters.countries}
          onChange={v => updateFilter('countries', v)}
        />
      </div>
    </div>
  );
};
